/**
 * Module dependencies.
 */

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  FormList,
  ListColumns
} from 'app/components/organisms/crud/form-types';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { apiEndpoints } from 'app/core/config/api-endpoints';
import { requiredRule } from 'app/core/utils/field-rules';
import { useCrudRequest } from 'app/hooks/requests/crud/use-crud-request';
import { PiBucket } from 'app/hooks/requests/pool-invest/use-pool-invest';
import { appRoutes } from 'app/routes';
import { CopyId } from 'app/components/atoms/copy-id/copy-id';
import { TFunction } from 'i18next';
import React from 'react';

/**
 * List columns.
 */

/**
 * Format a value in the project's currency (e.g. `150,00 €`).
 */

const money = (value: number, currency = 'EUR') =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);

const listColumns = (translate: TFunction): ListColumns => [
  {
    title: 'ID',
    dataIndex: 'id',
    size: '110px',
    key: 'id',
    render: (item: any) => <CopyId value={item.id} />
  },
  {
    title: translate('poolInvest.transactions.date'),
    dataIndex: 'date',
    size: '120px',
    key: 'date',
    render: (item: any) => (item.date ? String(item.date).slice(0, 10) : '—')
  },
  {
    title: translate('poolInvest.transactions.category'),
    dataIndex: 'category',
    size: '1fr',
    key: 'category',
    render: (item: any) => item.category || '—'
  },
  {
    title: translate('poolInvest.transactions.type'),
    dataIndex: 'direction',
    size: '90px',
    key: 'direction',
    render: (item: any) => translate(`poolInvest.direction.${item.direction}`)
  },
  {
    title: translate('poolInvest.transactions.amount'),
    dataIndex: 'amountFiat',
    size: '120px',
    key: 'amountFiat',
    render: (item: any) => (
      <span className={'flex flex-col leading-tight'}>
        <span>{money(Number(item.amountFiat), item.currency)}</span>

        {item.amountUsd != null ? (
          <span className={'text-xs text-muted-foreground'}>
            {Number(item.amountUsd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            {' $'}
          </span>
        ) : null}
      </span>
    )
  },
  {
    title: translate('poolInvest.transactions.participants'),
    dataIndex: 'participantCount',
    size: '220px',
    key: 'participants',
    render: (item: any) => {
      const shares = item.shares ?? [];

      if (!shares.length) {
        return '—';
      }

      return (
        <span className={'flex flex-col leading-tight'}>
          <span className={'font-medium'}>
            {item.participantCount} {translate('poolInvest.transactions.participants')}
          </span>

          {shares.map((share: any) => (
            <span className={'text-xs text-muted-foreground'} key={share.participantId}>
              {share.name || '—'}
              {share.weight != null && share.weight !== 1 ? ` (×${share.weight})` : ''}
              {': '}
              {money(Number(share.amountFiat), item.currency)}
            </span>
          ))}
        </span>
      );
    }
  },
  {
    title: translate('poolInvest.transactions.quantity'),
    dataIndex: 'quantity',
    size: '140px',
    key: 'quantity',
    render: (item: any) => (item.quantity ? Number(item.quantity).toString() : '—')
  },
  {
    title: translate('poolInvest.transactions.unitPrice'),
    dataIndex: 'unitPriceFiat',
    size: '120px',
    key: 'unitPriceFiat',
    render: (item: any) =>
      item.unitPriceFiat
        ? Number(item.unitPriceFiat).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '—'
  },
  {
    title: translate('poolInvest.transactions.currentValue'),
    dataIndex: 'currentValueFiat',
    size: '130px',
    key: 'currentValueFiat',
    render: (item: any) => {
      if (item.currentValueFiat == null) {
        return '—';
      }

      const now = Number(item.currentValueFiat);
      const invested = Number(item.amountFiat) || 0;
      const cls =
        invested && now > invested ? 'text-emerald-600' : invested && now < invested ? 'text-destructive' : '';

      return (
        <span className={'flex flex-col leading-tight'}>
          <span className={cls}>{money(now, item.currency)}</span>

          {item.currentValueUsd != null ? (
            <span className={'text-xs text-muted-foreground'}>
              {Number(item.currentValueUsd).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              {' $'}
            </span>
          ) : null}
        </span>
      );
    }
  },
  {
    title: translate('poolInvest.transactions.fee'),
    dataIndex: 'feeFiat',
    size: '160px',
    key: 'feeFiat',
    render: (item: any) =>
      item.feeFiat && Number(item.feeFiat) > 0 ? (
        <span>
          {Number(item.feeFiat).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          {item.feeNote ? (
            <span className={'text-xs text-muted-foreground'}>
              {' · '}
              {item.feeNote}
            </span>
          ) : null}
        </span>
      ) : (
        '—'
      )
  },
  {
    title: translate('poolInvest.transactions.note'),
    dataIndex: 'note',
    size: '1fr',
    key: 'note',
    render: (item: any) => item.note || '—'
  }
];

/**
 * Form fields (shared by add + edit).
 */

const formFields = (translate: TFunction): FormList => [
  {
    type: 'selectField',
    name: 'bucketId',
    label: translate('poolInvest.transactions.category'),
    rules: requiredRule(translate),
    options: {
      hook: useCrudRequest as any,
      hookProps: (_values: any, params: any) => [
        {
          key: ['poolInvestBuckets'],
          options: { interpolations: params },
          endpoint: apiEndpoints.poolInvestProjectBuckets
        }
      ],
      normalize: (items: PiBucket[]) => (items ?? []).map(item => ({ value: item.id, label: item.name }))
    }
  },
  {
    type: 'selectField',
    name: 'direction',
    label: translate('poolInvest.transactions.type'),
    rules: requiredRule(translate),
    options: [
      { value: 'in', label: translate('poolInvest.direction.in') },
      { value: 'out', label: translate('poolInvest.direction.out') }
    ]
  },
  {
    type: 'datePickerField',
    name: 'date',
    label: translate('poolInvest.transactions.date'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputNumberField',
    name: 'amountFiat',
    label: translate('poolInvest.transactions.amount'),
    rules: requiredRule(translate)
  },
  {
    type: 'inputField',
    name: 'quantity',
    label: translate('poolInvest.transactions.quantityHint'),
    rules: undefined
  },
  {
    type: 'inputNumberField',
    name: 'unitPriceFiat',
    label: translate('poolInvest.transactions.unitPrice'),
    rules: undefined
  },
  {
    type: 'inputNumberField',
    name: 'feeFiat',
    label: translate('poolInvest.transactions.fee'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'feeNote',
    label: translate('poolInvest.transactions.feeNote'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'note',
    label: translate('poolInvest.transactions.note'),
    rules: undefined
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: String(translate('common.labels.search'))
  }
];

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType => ({
  formFields: formFields(translate)
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType => ({
  formFields: formFields(translate)
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType => ({});

/**
 * List.
 */

const list = (translate: TFunction): CrudListType => ({
  columns: listColumns(translate),
  style: { minWidth: 700 },
  route: appRoutes.dashboard.poolInvest.projectTransactions,
  key: ['poolInvestEntries'],
  endpoint: apiEndpoints.poolInvestProjectEntries,
  filters: listFilters(translate)
});

/**
 * Config.
 */

export function createPoolInvestTransactionsTemplate(translate: TFunction): CrudTemplate {
  return normalizeTemplate(translate, {
    add: add(translate),
    edit: edit(translate),
    list: list(translate),
    remove: remove()
  });
}
